<script lang="js" setup>
import { ref, onMounted, computed } from "vue";
import { fetchGetApi } from "@/util/api";
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';
import { defineAsyncComponent } from 'vue';
import axios from 'axios';

const allData = ref([]);
const elementsPerPage = ref(25);
const currentPage = ref(1);
const filterQuery = ref("");
const toast = useToast();

const modalCode = defineAsyncComponent(() => import("@/components/roles/modalCode.vue"));

onMounted(async () => {
    fetchGetApi('admin/api/operation/get/all/reinstate').then((response) => {
        if (response && response.data && response.data.length > 0) {
            allData.value = response.data;
        } else {
            toast.error('Failed to fetch data');
        }
    });
});

function downloadExcel() {
    const worksheet = XLSX.utils.json_to_sheet(allData.value);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reinstate Data");
    XLSX.writeFile(workbook, 'reinstate_data.xlsx');
}

const filteredData = computed(() => {
    const query = filterQuery.value.toLowerCase();
    return allData.value.filter(kyt =>
        (kyt?.employeeName?.toLowerCase()?.includes(query) || false) ||
        (kyt?.lastSite?.toLowerCase()?.includes(query) || false)
    );
});

const paginatedData = computed(() => {
    const start = (currentPage.value - 1) * elementsPerPage.value;
    const end = start + elementsPerPage.value;
    return filteredData.value.slice(start, end);
});

function num_pages() {
    return Math.ceil(filteredData.value.length / elementsPerPage.value);
}

function change_page(page) {
    currentPage.value = page;
}

function previousPage() {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
}

function nextPage() {
    if (currentPage.value < num_pages()) {
        currentPage.value++;
    }
}

const baseUrl = process.env.VUE_APP_BASE_URL;

// Add date formatter function
function formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

async function handleAction(id, action, event) {
    // Prevent default form submission
    if (event) {
        event.preventDefault();
    }

    try {
        const result = await Swal.fire({
            title: `Confirm ${action}?`,
            text: `Are you sure you want to ${action} this reinstate request?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: action === 'approve' ? '#3085d6' : '#d33',
            cancelButtonColor: '#d33',
            confirmButtonText: `Yes, ${action} it!`
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.put(`${baseUrl}admin/api/operation/reinstate/${action}/${id}`, {}, {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });
                
                if (response.data && response.data.message) {
                    await Swal.fire({
                        title: 'Success!',
                        text: response.data.message,
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                    
                    // Refresh the data
                    const updatedResponse = await fetchGetApi('admin/api/operation/get/all/reinstate');
                    if (updatedResponse && updatedResponse.data) {
                        allData.value = updatedResponse.data;
                    }
                }
            } catch (error) {
                let errorMessage = 'An error occurred';
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                await Swal.fire({
                    title: 'Error!',
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: 'Ok'
                });
            }
        }
    } catch (error) {
        console.error('Error in handleAction:', error);
    }
}
</script>

<template>
    <Breadcrumbs main="Apps" title="Reinstate" />
    <div class="container-fluid">
        <div class="email-wrap bookmark-wrap">
            <div class="row card card-body">
                <div class="table-responsive product-table custom-scrollbar">
                    <form class="no-footer">
                        <div class="mb-3 row justify-content-between">
                            <div class="col-xs-3 col-sm-auto">
                                <label for="table-complete-search"
                                    class="col-xs-12 col-sm-auto col-form-label">Search:</label>
                                <input id="table-complete-search" type="text" class="form-control" name="searchTerm"
                                    v-model="filterQuery">
                            </div>
                            <div class="col-xs-3 col-sm-auto">
                                <button type="button" class="btn btn-primary" @click="downloadExcel">Download Excel</button>
                            </div>
                        </div>
                        <table class="display table-striped table-hover table-bordered table" id="basic-1">
                            <thead>
                                <tr>
                                    <th class="sorting_asc" scope="col" style="width: 50px;">SL</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Employee</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Last Site</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Department</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Designation</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Siddar By</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Job Tenures</th>
                                    <th class="sorting_asc text-nowrap" scope="col">No of Reinstate</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Lose to Company</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Reason</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Status</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Action By</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Action Time</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in paginatedData" :key="item._id">
                                    <td>{{ (currentPage - 1) * elementsPerPage + index + 1 }}</td>
                                    <td>{{ item?.employeeName || '-' }}</td>
                                    <td>{{ item?.lastSite || '-' }}</td>
                                    <td>{{ item?.department || '-' }}</td>
                                    <td>{{ item?.designation || '-' }}</td>
                                    <td>{{ item?.siddarByEmpId || '-' }}</td>
                                    <td>
                                        <span class="badge bg-primary me-1">{{ item?.jobTenures || 0 }} Days</span>
                                        <span class="badge bg-success me-1">{{ Math.floor((item?.jobTenures || 0) / 30) }} Months</span>
                                        <span class="badge bg-info">{{ Math.floor((item?.jobTenures || 0) / 365) }} Years</span>
                                    </td>
                                    <td>{{ item?.noOfReinstate || '-' }}</td>
                                    <td>{{ item?.loseToCompany || '-' }}</td>
                                    <td>{{ item?.reason || '-' }}</td>
                                    <td>
                                        <span :class="{
                                            'badge': true,
                                            'bg-warning': item?.status === 'Pending',
                                            'bg-success': item?.status === 'Approved',
                                            'bg-danger': item?.status === 'Rejected'
                                        }">
                                            {{ item?.status || 'Pending' }}
                                        </span>
                                    </td>
                                    <td>
                                        <span v-if="item?.approvalDetails?.approvedBy">
                                            {{ item.approvalDetails.approvedBy }}
                                        </span>
                                        <span v-else-if="item?.rejectionDetails?.rejectedBy">
                                            {{ item.rejectionDetails.rejectedBy }}
                                        </span>
                                        <span v-else>-</span>
                                    </td>
                                    <td>
                                        <span v-if="item?.approvalDetails?.approvedAt">
                                            {{ formatDate(item.approvalDetails.approvedAt) }}
                                        </span>
                                        <span v-else-if="item?.rejectionDetails?.rejectedAt">
                                            {{ formatDate(item.rejectionDetails.rejectedAt) }}
                                        </span>
                                        <span v-else>-</span>
                                    </td>
                                    <td class="text-nowrap">
                                        <button 
                                            v-if="item?.status === 'Pending'"
                                            class="btn btn-success btn-sm me-2" 
                                            @click.prevent="handleAction(item._id, 'approve', $event)"
                                        >
                                            Approve
                                        </button>
                                        <button 
                                            v-if="item?.status === 'Pending'"
                                            class="btn btn-danger btn-sm" 
                                            @click.prevent="handleAction(item._id, 'reject', $event)"
                                        >
                                            Reject
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <ul class="pagination py-2 justify-content-end pagination-primary">
                            <li class="page-item" @click="previousPage">
                                <a class="page-link">Previous</a>
                            </li>
                            <li class="page-item" v-for="i in num_pages()" :key="i"
                                :class="[i == currentPage ? 'active' : '']" @click="change_page(i)">
                                <a class="page-link">{{ i }}</a>
                            </li>
                            <li class="page-item" @click="nextPage">
                                <a class="page-link">Next</a>
                            </li>
                        </ul>
                    </form>
                    <modalCode />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.btn-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    border-radius: 0.2rem;
}

/* Add styles for the new columns */
td {
    vertical-align: middle;
}
</style>